// The MEASURE tool — the first floor domain slice (template for the rest).
// Owns the per-frame measuring gesture (snap → cursor preview → click to drop
// points), the measurement snapping rules, the Clear action, the total readout,
// and its own sidebar section (registered via ui/section-registry, shown only
// while the Measure tool is active). State lives in floor/core's measureSystem;
// floor-mode injects only what it still owns (shift state, the draw label, the
// undo commit, the active tool).
//
// snapMeasure is exported back to floor-mode: the Select tool re-snaps a
// dragged measurement VERTEX with the same rules (that chain is still in
// floor-mode pending E4).

import * as THREE from "three";
import { measureSystem, placementSystem, wallSystem, floorTools } from "../core";
import { closestOnSeg } from "../geom";
import { type VertexRef, type MeasurePill } from "../measure";
import { desktopMouse } from "../../input/desktop";
import { type SectionRegistry } from "../../ui/section-registry";
import { type FloorTool as FloorToolId } from "../../ui/floor-ui";

const MEASURE_SNAP = 0.2; // metres
// How close (perpendicular, in metres) the second point must be to a straight
// horizontal/vertical line through the first point before it snaps dead straight.
// A FIXED distance — not an angle — so the snap window doesn't widen as the
// measurement gets longer. Smaller = less aggressive.
const MEASURE_STRAIGHT = 0.05; // metres

export interface MeasureToolDeps {
  activeCamera: () => THREE.Camera; // segment picks raycast from the cursor
  sections: SectionRegistry; // the sidebar host (mounted by floor-ui)
  activeTool: () => FloorToolId; // section shows only while Measure is active
  isShiftHeld: () => boolean; // Shift bypasses ALL snapping
  hideDrawLabel: () => void; // the wall tool's floating label (shared overlay)
  commitFloor: () => void; // snapshot for undo (each segment / clear)
  // Select-chain integration (the vertex + segment domains below): zoom-aware
  // pick tolerance, and dropping every non-measure selection when a vertex or
  // segment is grabbed (wall/placement/opening + the selection UI refresh).
  markerScale: (x: number, z: number) => number;
  clearOtherSelections: () => void;
  occluderDist: (ray: THREE.Raycaster) => number; // side-elevation pick barrier
  closeDimEdit: () => void; // deleting a segment closes its in-place editor
  // A press on a measurement pill while the Measure tool is active opens that
  // pill's in-place length editor (instead of dropping a new point) — selecting
  // the segment and showing the edit box, exactly like a Select-tool dbl-click.
  editPill: (pill: MeasurePill) => void;
}

export interface MeasureToolApi {
  snapMeasure(
    p: { x: number; z: number },
    prev: { x: number; z: number } | null,
  ): { x: number; z: number; anchorId: number | null };
  clearMeasure(): void;
  refreshTotal(): void;
  // The measurement-VERTEX domain of the Select tool's gesture chain (press →
  // drag → release/cancel hooks; floor-mode calls them at the chain's original
  // priority spots). A plain click toggles the vertex lock; a drag moves it.
  pressVertex(fp: { x: number; z: number } | null): boolean;
  // The measurement-SEGMENT domain: press-select a segment (dots/pills/line),
  // exclusive selection, Backspace-delete.
  pressSegment(fp: { x: number; z: number } | null): boolean;
  selectSegment(sel: { chain: number; seg: number }): void;
  deleteSelectedSegment(): boolean;
  clearSegmentSelection(): void;
  dragVertex(fp: { x: number; z: number } | null): boolean;
  isVertexDragging(): boolean;
  clearVertexDrag(): void;
  releaseVertex(): void;
  cancelVertexGesture(): void;
}

export function installMeasureTool(deps: MeasureToolDeps): MeasureToolApi {
  // Measure snapping. Snaps the cursor onto wall faces + object surfaces, then
  // (relative to the previous point) locks to a right angle when the segment is
  // near horizontal/vertical. Shift bypasses ALL of it for a free measurement.
  function snapMeasure(
    p: { x: number; z: number },
    prev: { x: number; z: number } | null,
  ): { x: number; z: number; anchorId: number | null } {
    if (deps.isShiftHeld()) return { x: p.x, z: p.z, anchorId: null };
    // Surface snap: nearest point on a wall face or an object footprint edge.
    // Track which placement (if any) the winning edge belongs to, so the node can
    // be anchored to that model — wall snaps leave anchorId null (free node).
    let best: { x: number; z: number } | null = null;
    let bestD = MEASURE_SNAP;
    let bestId: number | null = null;
    for (const f of wallSystem.faceSegments()) {
      const c = closestOnSeg(p.x, p.z, f.ax, f.az, f.bx, f.bz);
      if (c.d < bestD) {
        bestD = c.d;
        best = { x: c.x, z: c.z };
        bestId = null;
      }
    }
    for (const f of placementSystem.footprintEdges()) {
      const c = closestOnSeg(p.x, p.z, f.ax, f.az, f.bx, f.bz);
      if (c.d < bestD) {
        bestD = c.d;
        best = { x: c.x, z: c.z };
        bestId = f.id;
      }
    }
    const q = best ?? { x: p.x, z: p.z };
    // Straight lock against the previous point: keep the dominant-axis coordinate
    // (often the surface-snapped one) and align the other to `prev` — but only when
    // the OFF-axis offset is within MEASURE_STRAIGHT metres of straight.
    if (prev) {
      const adx = Math.abs(q.x - prev.x);
      const adz = Math.abs(q.z - prev.z);
      if (adx >= adz && adz <= MEASURE_STRAIGHT) return { x: q.x, z: prev.z, anchorId: bestId }; // ~horizontal
      if (adz > adx && adx <= MEASURE_STRAIGHT) return { x: prev.x, z: q.z, anchorId: bestId }; // ~vertical
    }
    return { x: q.x, z: q.z, anchorId: bestId };
  }

  // The total/instruction readout, owned by this slice's sidebar section.
  let totalEl: HTMLElement | null = null;
  function refreshTotal(): void {
    // Single-segment measuring: each click-pair drops one measurement, shown as
    // its X and Z legs. No running chain total to report — just the instruction.
    if (totalEl) totalEl.textContent = "";
  }

  function clearMeasure(): void {
    measureSystem.clear();
    refreshTotal();
    deps.commitFloor(); // clearing measurements is undoable
  }

  // Sidebar section: the readout, visible only on the Measure tool.
  deps.sections.register({
    id: "floor-measure",
    build: (host) => {
      const grp = host.group();
      totalEl = document.createElement("span");
      totalEl.className = "hint";
      totalEl.style.flexBasis = "100%";
      totalEl.textContent = "Click points; Esc to finish.";
      grp.appendChild(totalEl);
      return {
        render: () => grp.classList.toggle("hidden", deps.activeTool() !== "measure"),
      };
    },
  });

  // A press that landed on a pill, remembered so the in-place editor opens on
  // RELEASE (not press): opening it while the button is still down lets the
  // ensuing pointerup/click move focus to the canvas and blur — instantly
  // closing — the edit box. Deferring to release mirrors the Select-tool
  // double-click, which opens only after every mouse event has fired.
  let pendingPillEdit: MeasurePill | null = null;

  // The per-frame gesture while the Measure tool is active.
  floorTools.register({
    id: "measure",
    frame: ({ fp, press, release }) => {
      wallSystem.hidePreview();
      deps.hideDrawLabel();
      // Park the snap-cursor preview when the pointer is over the toolbar (or
      // otherwise off the canvas) and we're not mid-drag: the floor point is
      // still computed from the last on-canvas NDC, so without this gate the
      // white ring lingers at the toolbar's edge instead of vanishing. A held
      // drag keeps following the mouse via `lmb` (overCanvas isn't set then).
      const onCanvas = desktopMouse.overCanvas || desktopMouse.lmb;
      if (fp && onCanvas) {
        // A measurement is a single segment. The second click may land anywhere; a
        // real diagonal is split into independent X and Z legs. But when the second
        // point is within MEASURE_STRAIGHT of a straight line through the first, snap
        // it dead straight so a near-horizontal/vertical measure doesn't leave a tiny
        // stub leg. Passing `prev` (the first point) enables that lock; it's null
        // before the first click, so the first point itself doesn't snap.
        const prev = measureSystem.lastPoint();
        const s = snapMeasure(fp, prev);
        measureSystem.setCursor(s, prev !== null);
        if (press) {
          // Press priority while the Measure tool is active: edit a pill > grab
          // an existing endpoint (move it / toggle its lock) > select an existing
          // segment > drop a new measurement point. The grab/select branches run
          // only when NOT mid-measurement (no first point dropped yet) — once
          // you've placed point 1, the next click always completes the segment.
          // This lets you select a measurement and lock/unlock its vertices
          // without leaving the Measure tool. The pill edit and any lock toggle
          // open/commit on release (see below), so the click's own pointerup
          // doesn't immediately blur the edit box.
          _segRay.setFromCamera(desktopMouse.ndc, deps.activeCamera());
          pendingPillEdit = measureSystem.pickLabel(_segRay, deps.occluderDist(_segRay));
          const building = prev !== null;
          if (pendingPillEdit) {
            // Pill edit opens on release.
          } else if (!building && pressVertex(fp)) {
            // Grabbed an existing endpoint: a drag moves it, a plain click
            // toggles its lock (resolved on release).
          } else if (!building && pressSegment(fp)) {
            // Selected an existing segment: Backspace deletes it; clicking its
            // pill edits the length.
          } else {
            // If the node landed on a model, pin it to that model in the model's
            // local frame so the measurement updates as the model is moved.
            let anchor: { id: number; lx: number; lz: number } | undefined;
            if (s.anchorId !== null) {
              const lo = placementSystem.worldToLocalXZ(s.anchorId, s.x, s.z);
              if (lo) anchor = { id: s.anchorId, lx: lo.lx, lz: lo.lz };
            }
            measureSystem.addPoint(s.x, s.z, anchor); // starts a fresh chain if finished
            refreshTotal();
            // Commit once a real segment exists (≥2 points). A lone first point is
            // provisional — finish() drops it — so it shouldn't dirty/undo-log.
            if (measureSystem.count() >= 2) deps.commitFloor(); // each segment is undoable
          }
        }
      } else {
        measureSystem.setCursor(null);
      }
      // Drag a grabbed endpoint (no-ops unless one was grabbed above and held).
      dragVertex(fp);
      if (release) {
        // Open the in-place pill editor only once the click has fully completed,
        // so the freshly-focused edit box isn't blurred by the same gesture's
        // pointerup. Otherwise resolve the vertex gesture: a drag committed a
        // move; a plain click toggles the endpoint's lock (releaseVertex commits
        // that itself), mirroring the Select-tool chain's release tail.
        const pill = pendingPillEdit;
        pendingPillEdit = null;
        if (pill) {
          deps.editPill(pill);
        } else {
          const wasDragging = isVertexDragging();
          clearVertexDrag();
          if (wasDragging) deps.commitFloor();
          releaseVertex();
        }
      }
    },
  });

  // ── Select-chain vertex domain ──────────────────────────────────────────
  // A measurement endpoint mirrors a wall corner: hovering whitens it; a plain
  // click (no drag) toggles its lock; an unlocked vertex drags to move.
  let pressVid: VertexRef | null = null; // node under the press
  let pressVidMoved = false;
  let dragVid: VertexRef | null = null; // node being dragged (null = locked)
  const _pressStart = new THREE.Vector2();
  const _segRay = new THREE.Raycaster();

  // Select-tool press on a measurement VERTEX (endpoint). Picked by a screen-
  // relative proximity (~15px) so the small dots are easy to hit, and BEFORE
  // the model under an anchored vertex — so clicking the dot grabs the vertex,
  // not the model. Grabbing a vertex drops every other selection.
  function pressVertex(fp: { x: number; z: number } | null): boolean {
    if (!fp) return false;
    const tol = deps.markerScale(fp.x, fp.z) * 0.27; // ≈15px at any zoom
    const v = measureSystem.pickEndpoint(fp.x, fp.z, tol);
    if (!v) return false;
    measureSystem.setSelected(null); // drop the segment-blue highlight
    deps.clearOtherSelections();
    pressVid = v;
    pressVidMoved = false;
    _pressStart.set(desktopMouse.clientX, desktopMouse.clientY);
    dragVid = measureSystem.vertexLocked(v.chain, v.idx) ? null : v;
    return true;
  }

  // Drag a measurement endpoint (claims the frame only while actually armed +
  // held + on the floor, like the original else-if gate). Dead-zone first so a
  // plain click (to select / lock) doesn't nudge it. Re-snaps to models/walls/
  // grid like placing a measure point: landing on a model re-anchors the node
  // (so it tracks that model); elsewhere it's a free point.
  function dragVertex(fp: { x: number; z: number } | null): boolean {
    if (dragVid === null || !desktopMouse.lmb || !fp) return false;
    if (
      !pressVidMoved &&
      Math.hypot(
        desktopMouse.clientX - _pressStart.x,
        desktopMouse.clientY - _pressStart.y,
      ) > 4
    ) {
      pressVidMoved = true;
    }
    if (pressVidMoved) {
      const s = snapMeasure(fp, null);
      let anchor: { id: number; lx: number; lz: number } | undefined;
      if (s.anchorId !== null) {
        const lo = placementSystem.worldToLocalXZ(s.anchorId, s.x, s.z);
        if (lo) anchor = { id: s.anchorId, lx: lo.lx, lz: lo.lz };
      }
      measureSystem.setNode(dragVid.chain, dragVid.idx, s.x, s.z, anchor);
      refreshTotal();
    }
    return true;
  }

  const isVertexDragging = (): boolean => dragVid !== null;
  const clearVertexDrag = (): void => {
    dragVid = null;
  };
  // Release tail: a press that never became a drag is a click → toggle the
  // vertex's lock (yellow ↔ red). A drag just moved it → no toggle.
  function releaseVertex(): void {
    if (pressVid !== null && !pressVidMoved) {
      measureSystem.togglePointLock(pressVid.chain, pressVid.idx);
      deps.commitFloor();
    }
    pressVid = null;
    pressVidMoved = false;
  }
  function cancelVertexGesture(): void {
    dragVid = null;
    pressVid = null;
    pressVidMoved = false;
  }

  // ── Select-chain segment domain ──────────────────────────────────────────
  // Select a measurement segment (Select tool). Exclusive with every other
  // selection (incl. a measurement vertex) so Backspace has one unambiguous
  // target.
  function selectSegment(sel: { chain: number; seg: number }): void {
    deps.clearOtherSelections();
    measureSystem.setSelected(sel);
  }
  // Select-tool press on a measurement: endpoint dots and midpoint pills raycast
  // directly; the thin ribbon line picks within a zoom-scaled tolerance of the
  // floor point. A dot selects its adjacent segment (the dot itself is the
  // click-again lock target). Returns false when nothing measurable was hit.
  function pressSegment(fp: { x: number; z: number } | null): boolean {
    const tol = fp ? 4 * 0.05 * deps.markerScale(fp.x, fp.z) : 0;
    _segRay.setFromCamera(desktopMouse.ndc, deps.activeCamera());
    // Don't pick measurements on the far side of a door being edited head-on.
    const hit = measureSystem.pick(_segRay, fp?.x ?? null, fp?.z ?? null, tol, deps.occluderDist(_segRay));
    if (!hit) return false;
    selectSegment(
      hit.kind === "segment"
        ? { chain: hit.chain, seg: hit.seg }
        : { chain: hit.chain, seg: Math.max(0, hit.idx - 1) },
    );
    return true;
  }
  // Delete the selected measurement segment (Backspace/Delete). The chain splits
  // at the removed span. Returns false when no measurement is selected.
  function deleteSelectedSegment(): boolean {
    if (!measureSystem.selected()) return false;
    deps.closeDimEdit();
    measureSystem.deleteSelected();
    refreshTotal();
    deps.commitFloor();
    return true;
  }

  return {
    snapMeasure,
    clearMeasure,
    refreshTotal,
    pressVertex,
    dragVertex,
    isVertexDragging,
    clearVertexDrag,
    releaseVertex,
    cancelVertexGesture,
    pressSegment,
    selectSegment,
    deleteSelectedSegment,
    clearSegmentSelection: () => measureSystem.setSelected(null),
  };
}
